/**
 * Shared helpers for the Axios-normalised error shape produced by the
 * api.client response interceptor. The interceptor converts `AxiosError`
 * instances into plain objects `{ status, code, message, requestId?, details? }`
 * (api-design.md §7 standardised envelope). Mutation + query `onError`
 * callbacks were previously duplicating this shape check 4–5 places — this
 * module is the one place the shape assumption lives.
 */

export interface NormalisedApiError {
  status?: number;
  code?: string;
  message?: string;
  requestId?: string;
  details?: string[];
}

function asNormalised(err: unknown): NormalisedApiError {
  if (err !== null && typeof err === 'object') {
    return err as NormalisedApiError;
  }
  return {};
}

/** Returns the API error's HTTP status if present, else undefined. */
export function getApiErrorStatus(err: unknown): number | undefined {
  const status = asNormalised(err).status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Returns the error's human-readable message, or `fallback` if none. Passing a
 * fallback specific to the action (e.g. "Failed to load users") beats generic
 * defaults — it survives when the message field is missing.
 */
export function extractApiErrorMessage(
  err: unknown,
  fallback = 'Something went wrong. Please try again.',
): string {
  const normalised = asNormalised(err);
  return typeof normalised.message === 'string' && normalised.message.length > 0
    ? normalised.message
    : fallback;
}
